import { useLazyQuery, useQuery } from '@apollo/client/react'
import { ArrowUpDown, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_SCORM_TRACKINGS } from '~/gql/scorm/scorm'
import { client } from '~/helpers/apollo'
import { returnOrderBy } from '~/pages/backoffice/sorter'
import { downloadCSV } from '~/utils/exportCsv/utils'

type SortedInfo = {
  field: string
  order: 'ascend' | 'descend'
}

// Plafond d'heures plausible (1 an) : au-delà, la durée est aberrante (donnée
// legacy corrompue) et on n'affiche rien plutôt qu'un "666667h".
const MAX_PLAUSIBLE_HOURS = 366 * 24

function formatDuration(iso: string | null | undefined): string {
  if (!iso) return '-'
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
  if (!match) return iso
  if (match[1] && Number.parseInt(match[1], 10) > MAX_PLAUSIBLE_HOURS) return '-'
  const h = match[1] ? `${match[1]}h ` : ''
  const m = match[2] ? `${match[2]}m ` : ''
  const s = match[3] ? `${match[3]}s` : ''
  return `${h}${m}${s}`.trim() || '-'
}

// Affiche "raw / max" arrondi, sans notation scientifique. Renvoie null si une
// valeur est absente ou aberrante (donnée legacy corrompue, ex. 3.99e+49).
function formatScore(raw: number | null | undefined, max: number | null | undefined): string | null {
  if (raw == null || max == null) return null
  if (!Number.isFinite(raw) || !Number.isFinite(max)) return null
  if (Math.abs(raw) > 1_000_000 || Math.abs(max) > 1_000_000) return null
  const fmt = (n: number) => Number.parseFloat(n.toFixed(2)).toString()
  return `${fmt(raw)} / ${fmt(max)}`
}

const EXPORT_HEADERS = [
  'Login',
  'Prénom',
  'Nom',
  'mail',
  'Date de début',
  'Date de fin',
  'Groupe',
  'Date de première connexion',
  'Date de dernière connexion',
  'Actif',
  'Societe',
  'Service',
  'Nom de l\'UA 1',
  'Etat de l\'UA 1',
  'Score de l\'UA 1',
  'Progression de l\'UA 1',
  'Temps de l\'UA 1',
  'Nombre de passages 1',
  'Date de première connexion 1',
  'Date de la dernière connexion 1',
  'Date de validation 1',
]

function completionLabel(status: string | null | undefined): string {
  switch (status) {
    case 'COMPLETED': return 'Complet'
    case 'INCOMPLETE': return 'Incomplet'
    case 'NOT_ATTEMPTED': return 'Non commencé'
    default: return 'Inconnu'
  }
}

function successLabel(status: string | null | undefined): string {
  switch (status) {
    case 'PASSED': return 'Passé'
    case 'FAILED': return 'Échoué'
    default: return 'Inconnu'
  }
}

function formatDateFr(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function StatisticsTable() {
  const [page, setPage] = useState<number>(1)
  const [sortedInfo, setSortedInfo] = useState<SortedInfo | undefined>()
  const [pageSize] = useState<number>(10)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const { t } = useTranslation()

  // Débounce la recherche : la requête ne part qu'après une pause de saisie,
  // pour éviter une requête par frappe (et le ThrottlerException associé).
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText)
      setPage(1)
    }, 400)
    return () => clearTimeout(handler)
  }, [searchText])

  // cache-first : revenir sur une page déjà chargée est servi depuis le cache
  // (zéro requête réseau), ce qui évite de saturer le throttler en navigation rapide.
  const { data: currentData, previousData, refetch, loading } = useQuery(GQL_SCORM_TRACKINGS, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        skip: page <= 1 ? 0 : (page - 1) * 10,
        take: pageSize,
        searchInput: debouncedSearch,
        orderBy: sortedInfo && returnOrderBy(sortedInfo.field, sortedInfo.order),
      },
    },
  })

  // Conserve l'affichage précédent tant que la nouvelle page charge ou en cas
  // d'erreur ponctuelle (ex. throttle) : le tableau ne se vide plus brutalement.
  const data = currentData ?? previousData

  const [fetchExportedTrackings] = useLazyQuery(GQL_SCORM_TRACKINGS)

  const handleSort = (field: string) => {
    setSortedInfo({
      field,
      order: sortedInfo?.order === 'ascend' ? 'descend' : 'ascend',
    })
  }

  async function exportCsv() {
    const exported = await fetchExportedTrackings({
      variables: {
        input: {
          skip: 0,
          take: 9999,
          searchInput: debouncedSearch,
        },
      },
    })
    if (!exported.data?.getScormTrackings.trackings) return

    downloadCSV(
      t('common.statistics'),
      EXPORT_HEADERS,
      exported.data.getScormTrackings.trackings.map((tr) => {
        const score = tr.scoreRaw != null && tr.scoreMax != null
          ? `${tr.scoreRaw}/${tr.scoreMax}`
          : ''
        const etat = `${completionLabel(tr.completionStatus)} (${successLabel(tr.successStatus)})`
        const totalTime = tr.totalTime ? formatDuration(tr.totalTime) : ''
        const validation = formatDateFr(tr.completedAt as unknown as string | null | undefined)
        return [
          /* Login */ tr.userEmail ?? '',
          /* Prénom */ tr.userFirstName ?? '',
          /* Nom */ tr.userLastName ?? '',
          /* mail */ tr.userEmail ?? '',
          /* Date de début */ '',
          /* Date de fin */ '',
          /* Groupe */ '',
          /* Date de première connexion */ '',
          /* Date de dernière connexion */ '',
          /* Actif */ '',
          /* Societe */ '',
          /* Service */ '',
          /* Nom de l'UA 1 */ '',
          /* Etat de l'UA 1 */ etat,
          /* Score de l'UA 1 */ score,
          /* Progression de l'UA 1 */ '',
          /* Temps de l'UA 1 */ totalTime,
          /* Nombre de passages 1 */ '',
          /* Date de première connexion 1 */ '',
          /* Date de la dernière connexion 1 */ '',
          /* Date de validation 1 */ validation,
        ]
      }),
    )
  }

  const getCompletionBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge variant="success">{t('status.completed')}</Badge>
      case 'INCOMPLETE':
        return <Badge variant="warning">{t('status.incomplete')}</Badge>
      case 'NOT_ATTEMPTED':
        return <Badge variant="secondary">{t('status.not_attempted')}</Badge>
      default:
        return <Badge variant="secondary">{t('status.unknown')}</Badge>
    }
  }

  const getSuccessBadge = (status: string) => {
    switch (status) {
      case 'PASSED':
        return <Badge variant="success">{t('status.passed')}</Badge>
      case 'FAILED':
        return <Badge variant="destructive">{t('status.failed')}</Badge>
      default:
        return <Badge variant="secondary">{t('status.unknown')}</Badge>
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Input
            placeholder={t('common.search')}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="max-w-sm"
          />
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="size-4" />
          </Button>
        </div>
        <Button variant="outline" onClick={exportCsv}>
          {t('common.export')}
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('userEmail')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.email')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('userFirstName')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.firstname')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('userLastName')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.lastname')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('completionStatus')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.completionStatus')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('successStatus')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.successStatus')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('scoreRaw')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.score')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('completedAt')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.completedAt')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                {t('common.totalTime')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading
              ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      {t('common.loading')}
                      ...
                    </TableCell>
                  </TableRow>
                )
              : !data?.getScormTrackings.trackings?.length
                  ? (
                      <TableRow>
                        <TableCell colSpan={8} className="h-24 text-center">
                          {t('common.empty')}
                        </TableCell>
                      </TableRow>
                    )
                  : (
                      data.getScormTrackings.trackings.map(tr => (
                        <TableRow key={tr.id}>
                          <TableCell>{tr.userEmail}</TableCell>
                          <TableCell>{tr.userFirstName}</TableCell>
                          <TableCell>{tr.userLastName}</TableCell>
                          <TableCell>{getCompletionBadge(tr.completionStatus)}</TableCell>
                          <TableCell>{getSuccessBadge(tr.successStatus)}</TableCell>
                          <TableCell>
                            {formatScore(tr.scoreRaw, tr.scoreMax) ?? '-'}
                          </TableCell>
                          <TableCell>
                            {tr.completedAt
                              ? new Date(tr.completedAt as unknown as string).toLocaleDateString('fr-FR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })
                              : '-'}
                          </TableCell>
                          <TableCell>{formatDuration(tr.totalTime)}</TableCell>
                        </TableRow>
                      ))
                    )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {`${data?.getScormTrackings.total ?? 0} ${t('common.results')}`}
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(old => Math.max(old - 1, 1))}
            disabled={page === 1}
          >
            {t('common.previous')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(old => old + 1)}
            disabled={!!(data?.getScormTrackings.total && (page * pageSize >= data?.getScormTrackings.total))}
          >
            {t('common.next')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default StatisticsTable
